import React, { useContext, useEffect, useState, Suspense } from 'react';
import { bool, string } from 'prop-types';
import classNames from 'classnames';
import { Carousel } from '@thd-olt-component-react/carousel';
import { Image } from '@thd-olt-component-react/core-ui';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { useLazyDataModel, useDataModel } from '@thd-nucleus/data-sources';
import { componentReady, thumbnailClick } from '../../../publisher';
import Helper from '../../Helper';
import './media-gallery-carousel.style.scss';
import '../../media-gallery.style.scss';
import { dataModel } from '../../dataModel';

const MediaGalleryOverlay = React.lazy(() => import(/* webpackChunkName: "media-gallery-overlay" */'../MediaGalleryOverlay'));
const MediaGalleryOverlayMobile = React.lazy(() => import(/* webpackChunkName: "media-gallery-overlay" */'../MediaGalleryOverlayMobile'));
export const MediaGalleryCarousel = ({ itemId, hover }) => {
  const experienceContext = useContext(ExperienceContext);
  const { channel } = experienceContext;
  const isMobile = channel === 'mobile';

  const [mainAsset, setMainAsset] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [hoverId, setHoverId] = useState('');

  useEffect(() => {
    if (hover) {
      window.LIFE_CYCLE_EVENT_BUS.on('supersku.hover', (hoverItemId) => {
        setHoverId(hoverItemId?.output);
      });
    }
  }, [hover]);

  const [hoveredProductRequest, hoveredProductResponse] = useLazyDataModel('product', {
    variables: { itemId: hoverId }
  });

  useEffect(() => {
    if (hoverId) {
      hoveredProductRequest({
        variables: {
          hoverId
        }
      });
    }
  }, [hoverId, hoveredProductRequest]);

  useEffect(() => {
    componentReady({ itemId });
  }, [itemId]);

  // for swatch Hover
  const mainHover = Helper.getMainHover({
    hoveredProductResponse
  });
  const hasItemId = hoverId || itemId;
  const productData = useDataModel('product', {
    variables: {
      itemId,
    },
    skip: !hasItemId
  });

  const { product = {} } = productData.data || {};
  const { brandName, productLabel } = product.identifiers || {};
  const { displayedMedia, ...apiResults } = Helper.getDisplayedMedia({
    isMobile, itemId, productData
  });
  if (apiResults.apiState) {
    return apiResults.returnStatement;
  }
  const { thumbnails, main, zoom } = displayedMedia;

  const handleImageClick = (media, mediaIndex) => {
    thumbnailClick({ primaryCategory: 'overlay', eventName: 'expanded view' });
    if (typeof window !== 'undefined') {
      window.history.pushState({ page: 'MEDIAOVERLAY' }, null, '');
    }
    setMainAsset(media);
    setSelectedAsset(mediaIndex);
  };

  const onChangeImage = (index) => {
    setSelectedImageIndex(index);
  };

  return (
    <>
      <Carousel
        dotBelow
        itemWidthDesktop={!isMobile ? '67%' : '20%'}
        itemWidthMobile={isMobile ? '100%' : '67%'}
        multiItem={false}
        onChange={onChangeImage}
        showArrows={!isMobile}
        showDots
      >
        {
          (displayedMedia.main).map((media, mediaIndex) => {
            const {
              shortDescription, type, url, mediaStill, videoStill
            } = media;
            const isVideo = Helper.isVideo({ media });
            const isThreeSixty = Helper.isImage360({ media });
            const isImage = Helper.isImage({ media });
            const isAR = Helper.isAR({ media });
            const isHotspot = Helper.isHotspot({ media });
            let imageUrl = url;
            let index = 0;
            if (mediaIndex === selectedImageIndex) {
              if (isImage) {
                index = mainHover.findIndex((t) => t.type === main[selectedImageIndex].type);
                if (index > 0 && mainHover[index]) {
                  imageUrl = mainHover[index].url;
                }
                if (index <= 0 && mainHover[0]) {
                  imageUrl = mainHover[0].url;
                }
              } else if (isVideo) {
                imageUrl = mainHover[0] ? mainHover[0].url : videoStill;
              } else if (isThreeSixty || isAR) {
                imageUrl = mainHover[0] ? mainHover[0].url : mediaStill;
              }
            } else if (isVideo) {
              imageUrl = videoStill;
            } else if (isThreeSixty || isAR) {
              imageUrl = mediaStill;
            }
            const videoScriptContent = JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'VideoObject',
              name: productLabel,
              description: shortDescription,
              thumbnailUrl: [videoStill],
              contentUrl: url
            });
            const carouselItemClassName = classNames('media-gallery-carousel', { 'media-gallery-carousel--border': !isMobile });
            return (
              <div className={carouselItemClassName} key={mediaIndex}>
                <div className="media-gallery-carousel__slide">
                  <Image
                    onClick={() => handleImageClick(media, mediaIndex)}
                    className="media-gallery-carousel__slide--active"
                    alt={`${brandName} - ${productLabel}`}
                    src={imageUrl}
                    lazy
                  />
                  {isVideo && (!mainHover[0] || mediaIndex !== selectedImageIndex)
                  && (
                    <>
                      <span
                        className="media-gallery-carousel__video-icon"
                      />
                      {/* eslint-disable-next-line react/no-danger */}
                      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: videoScriptContent }} />
                    </>
                  )}
                  {isThreeSixty && (!mainHover[0] || mediaIndex !== selectedImageIndex)
                  && <span className="media-gallery-carousel__three-sixty-icon" />}
                  {isAR && (!mainHover[0] || mediaIndex !== selectedImageIndex)
                  && (
                    <>
                      <span className="background-circle background-circle--main" />
                      <span className="ar-icon ar-icon--main" />
                    </>
                  )}
                  {isHotspot && (
                    <>
                      <span className="background-circle background-circle--main" />
                      <span className="hotspot-tag-icon hotspot-tag-icon__main" />
                    </>
                  )}
                </div>
              </div>
            );
          })
        }
      </Carousel>
      { !!mainAsset && !isMobile
      && (
        <Suspense fallback={(<div />)}>
          <MediaGalleryOverlay
            handleClose={() => setMainAsset(null)}
            overlayOpened={!!mainAsset}
            media={displayedMedia}
            product={product}
            mainAsset={mainAsset}
            thumbnails={thumbnails}
          />
        </Suspense>
      )}
      {!!mainAsset && isMobile && (
        <Suspense fallback={(<div />)}>
          <MediaGalleryOverlayMobile
            handleClose={() => setMainAsset(null)}
            overlayOpened={!!mainAsset}
            product={product}
            mainImage={mainAsset}
            mainImages={main}
            onMediaClick={handleImageClick}
            zoomedImages={zoom}
            thumbnails={thumbnails}
            selectedMainImageIndex={selectedAsset}
            selectedThumbnailIndex={selectedAsset}
          />
        </Suspense>
      )}
    </>
  );
};

MediaGalleryCarousel.displayName = 'MediaGalleryCarousel';

MediaGalleryCarousel.propTypes = {
  hover: bool,
  itemId: string.isRequired
};
MediaGalleryCarousel.defaultProps = {
  hover: false
};

MediaGalleryCarousel.dataModel = dataModel;
